import {
  LocaleService,
  ProcureDateAdapter,
  ProcureTitleStrategy,
} from '@abb-procure/common';
import {
  DEFAULT_DIALOG_WIDTH,
  ENVIRONMENT_CONFIG,
  ENVIRONMENT_INFO,
} from '@abb-procure/constants';
import { GlobalErrorHandlerService } from '@abb-procure/error-handling';
import {
  type CloseScrollStrategy,
  Overlay,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  type EnvironmentProviders,
  ErrorHandler,
  inject,
  LOCALE_ID,
  type Provider,
  provideAppInitializer,
} from '@angular/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { DateAdapter } from '@angular/material/core';
import { MAT_DATEPICKER_SCROLL_STRATEGY } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MAT_DIALOG_SCROLL_STRATEGY,
} from '@angular/material/dialog';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { TitleStrategy } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  type MsalGuardConfiguration,
  MsalInterceptor,
  type MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  type IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { buildInfo } from './build-info';

interface BuildInfo {
  timestamp?: string;
}

const httpInterceptorProviders: Provider[] = [
  { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
];

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.instance,
      redirectUri: environment.msalConfig.redirectUri ?? '/',
      postLogoutRedirectUri:
        environment.msalConfig.postLogoutRedirectUri ?? '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
        loggerCallback: environment.msalLoggingEnabled
          ? (
              logLevel: LogLevel,
              message: string,
              containsPii: boolean,
            ): void => {
              if (containsPii) {
                return;
              }

              switch (logLevel) {
                case LogLevel.Error:
                  console.error(message);
                  return;
                case LogLevel.Info:
                  console.info(message);
                  return;
                case LogLevel.Verbose:
                  console.debug(message);
                  return;
                case LogLevel.Warning:
                  console.warn(message);
                  return;
                default:
                  console.log(message);
              }
            }
          : undefined,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(`${environment.apiEndpoint}*`, [
    `api://${environment.msalConfig.apiEndpointId}/read_as_user`,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    authRequest: {
      scopes: ['user.read'],
    },
    interactionType: InteractionType.Redirect,
  };
}

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return (): CloseScrollStrategy => overlay.scrollStrategies.close();
}

export const APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  {
    provide: ErrorHandler,
    useClass: GlobalErrorHandlerService,
  },
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory,
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory,
  },
  {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory,
  },
  MsalService,
  MsalBroadcastService,
  MsalGuard,
  provideAppInitializer(() => inject(MsalService).initialize()),
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: {
      width: DEFAULT_DIALOG_WIDTH,
      disableClose: true,
      autoFocus: false,
      hasBackdrop: true,
    },
  },
  {
    provide: LOCALE_ID,
    useFactory: (localeService: LocaleService) => localeService.getLocale(),
    deps: [LocaleService],
  },
  {
    provide: MAT_DATEPICKER_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: scrollFactory,
  },
  {
    provide: MAT_SELECT_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: scrollFactory,
  },
  {
    provide: ENVIRONMENT_CONFIG,
    useValue: environment,
  },
  {
    provide: ENVIRONMENT_INFO,
    useValue: {
      id: environment.title.toLowerCase(),
      title: environment.title,
      buildInfo: {
        timestamp:
          (buildInfo as BuildInfo).timestamp ?? new Date().toISOString(),
      },
    },
  },
  {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: {
      disableTooltipInteractivity: true,
    },
  },
  {
    provide: MAT_DIALOG_SCROLL_STRATEGY,
    useFactory: (options: ScrollStrategyOptions) => options.block,
    deps: [ScrollStrategyOptions],
  },
  {
    provide: DateAdapter,
    useClass: ProcureDateAdapter,
  },
  {
    provide: MAT_TABS_CONFIG,
    useValue: {
      animationDuration: '350ms',
      stretchTabs: false,
    },
  },
  {
    provide: TitleStrategy,
    useClass: ProcureTitleStrategy,
  },
  {
    provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
    useValue: {
      hideSingleSelectionIndicator: true,
      hideMultipleSelectionIndicator: true,
    },
  },
  ...httpInterceptorProviders,
];
